import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import {
  ReportDetail,
  ReportMenu,
  ReportingMenuResponse,
} from '../../workspace-v2/analytics/reporting-v2/types/reporting.types';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ReportingService {
  selectedMenuPath = new BehaviorSubject<ReportMenu[]>([]);
  private readonly menuState = new BehaviorSubject<ReportingMenuResponse>(null);
  private readonly isLoading = new BehaviorSubject<boolean>(false);
  private menuPathCache: Record<string, ReportMenu[]> = {};
  private reportPathCache: Record<string, ReportMenu[]> = {};

  constructor(private readonly apiService: ApiService) {}

  getMenuState(): Observable<ReportingMenuResponse> {
    return this.menuState.asObservable();
  }

  getLoadingState(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  getSelectedMenuPath(): Observable<ReportMenu[]> {
    return this.selectedMenuPath.asObservable();
  }

  findMenuPathById(menuId: string): ReportMenu[] {
    if (this.menuPathCache[menuId]) {
      return this.menuPathCache[menuId];
    }

    if (!this.menuState.value?.menus) return [];

    const findPath = (menus: ReportMenu[], targetId: string, currentPath: ReportMenu[] = []): ReportMenu[] | null => {
      for (const menu of menus) {
        const newPath = currentPath.concat(menu);

        if (menu.id === targetId) {
          return newPath;
        }

        if (menu.subMenus?.length > 0) {
          const foundPath = findPath(menu.subMenus, targetId, newPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };

    const path = findPath(this.menuState.value.menus, menuId) || [];
    this.menuPathCache[menuId] = path;
    return path;
  }

  findMenuPathByReportId(reportId: string): ReportMenu[] {
    if (this.reportPathCache[reportId]) {
      return this.reportPathCache[reportId];
    }

    if (!this.menuState.value?.menus) return [];

    const findPath = (menus: ReportMenu[], targetId: string, currentPath: ReportMenu[] = []): ReportMenu[] | null => {
      for (const menu of menus) {
        const newPath = currentPath.concat(menu);
        if (menu.items?.some((item) => item.id === targetId)) {
          return newPath;
        }

        if (menu.subMenus?.length > 0) {
          const foundPath = findPath(menu.subMenus, targetId, newPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };

    const path = findPath(this.menuState.value.menus, reportId) || [];
    this.reportPathCache[reportId] = path;
    return path;
  }

  getReportMenus(refetch = false): Observable<ReportingMenuResponse> {
    if (this.menuState.value && !refetch) {
      return of(this.menuState.value);
    }

    this.isLoading.next(true);
    return this.apiService.httpGet('/api/dashboards', true).pipe(
      map((response: any) => response as ReportingMenuResponse),
      tap((response) => {
        this.menuState.next(response);
        this.isLoading.next(false);
      }),
      catchError((error) => {
        console.error('Error fetching report menus:', error);
        this.isLoading.next(false);
        throw error;
      })
    );
  }

  refreshMenus(): Observable<ReportingMenuResponse> {
    this.isLoading.next(true);
    return this.apiService.httpGet('/api/dashboards', true).pipe(
      map((response: any) => response as ReportingMenuResponse),
      tap((response) => {
        this.menuState.next(response);
        this.isLoading.next(false);
      }),
      catchError((error) => {
        console.error('Error refreshing report menus:', error);
        this.isLoading.next(false);
        throw error;
      })
    );
  }

  getReportData(reportId: string): Observable<ReportDetail> {
    return this.apiService
      .httpGet(`/api/dashboards/${reportId}`, true)
      .pipe(map((response: any) => response as ReportDetail));
  }

  clearCache(): void {
    this.menuState.next(null);
    this.selectedMenuPath.next([]);
    this.menuPathCache = {};
    this.reportPathCache = {};
  }

  clearMenuState(): void {
    this.menuState.next(null);
  }
}
